import { createFileRoute } from '@tanstack/react-router';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
// ⚡ Path-based imports (6× faster than barrel imports)
import UploadIcon from '@mui/icons-material/CloudUpload';
import MusicIcon from '@mui/icons-material/MusicNote';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import WaveIcon from '@mui/icons-material/GraphicEq';
import SpeedIcon from '@mui/icons-material/Speed';
import RefreshIcon from '@mui/icons-material/Refresh';
import DevicesIcon from '@mui/icons-material/Devices';
import { SpotifyIcon } from '@/components/icons/SpotifyIcon';
import { useState, useEffect } from 'react';
import { AudioFeaturesChart } from '@/components/AudioFeaturesChart';
import { AudioTimelineChart } from '@/components/AudioTimelineChart';
import { SpotifySearch } from '@/components/SpotifySearch';
import { SpotifyMatchSuggestions } from '@/components/SpotifyMatchSuggestions';
import { PlaybackFooter } from '@/components/PlaybackFooter';
import { useSpotifyPlayback } from '@/hooks/useSpotifyPlayback';
import { extractAudioMetadata, buildSpotifySearchQuery, type AudioMetadata } from '@/lib/audioMetadata';
import { describeAnalysisJob, formatTempoChip, formatTempoPrimary, formatTempoRange, isCustomAnalysis, runAudioAnalysis, type AnalysisJob, type ParsedAudioAnalysis } from '@/lib/audioAnalysis';
import { pageColors, getGradient } from '@/theme/colors';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { analytics } from '@/lib/analytics';
import { createPageMeta, PAGE_KEYWORDS } from '@/lib/meta';

// Musical note names for key visualization (0-11 corresponds to C-B)
const NOTE_NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];

export const Route = createFileRoute('/music')({
  component: MusicAnalyzerPage,
  head: () => {
    const base = createPageMeta({
      title: 'Music Analyzer - Audio Analysis & Spotify Integration | Christian Battaglia',
      description: 'Professional audio analysis tool powered by librosa and FastAPI. Upload audio files or connect with Spotify to analyze tempo, key, time signature, beats, and more.',
      path: '/music',
      image: '/og/music.png',
      keywords: PAGE_KEYWORDS.music,
    });
    return {
      meta: base.meta,
      links: base.links,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Music Analyzer',
            applicationCategory: 'MultimediaApplication',
            applicationSubCategory: 'Audio Analysis Tool',
            description: 'Professional audio analysis tool powered by librosa and FastAPI. Analyzes tempo, key, time signature, beats, and provides real-time visualization of waveforms and spectrograms.',
            operatingSystem: 'Web Browser',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            author: { '@type': 'Person', '@id': 'https://cdbattaglia.com/#person', name: 'Christian Battaglia' },
            keywords: PAGE_KEYWORDS.music.join(', '),
            featureList: ['Tempo (BPM) detection', 'Musical key detection', 'Time signature analysis', 'Beat and bar tracking', 'Waveform visualization', 'Spectrogram generation', 'Spotify integration', 'Real-time audio features', 'Metadata extraction'],
            screenshot: 'https://cdbattaglia.com/assets-cdbattaglia/media/images/music-analyzer-screenshot.png',
          }),
        },
      ],
    };
  },
});

type AnalysisResult = ParsedAudioAnalysis;

function MusicAnalyzerPage() {
  const { mode: themeMode } = useThemeContext();
  const { spotifyToken } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysisJob, setAnalysisJob] = useState<AnalysisJob | null>(null);

  // Spotify state
  const [selectedTrack, setSelectedTrack] = useState<any>(null);
  const [mode, setMode] = useState<'upload' | 'spotify'>('upload');

  // Audio metadata & matching
  const [audioMetadata, setAudioMetadata] = useState<AudioMetadata | null>(null);
  const [spotifySearchQuery, setSpotifySearchQuery] = useState<string>('');
  const [showSpotifyMatches, setShowSpotifyMatches] = useState(false);

  // Spotify playback
  const {
    isReady,
    isPlaying,
    currentTrack,
    position,
    volume,
    devices,
    activeDeviceId,
    playTrack,
    pause,
    resume,
    seek,
    setVolume,
    fetchDevices,
    switchDevice,
  } = useSpotifyPlayback(spotifyToken || undefined);

  // Note: Player auto-connects in useSpotifyPlayback hook
  // No manual connect() needed anymore

  useEffect(() => {
    if (spotifyToken) {
      fetch('https://api.spotify.com/v1/me', {
        headers: { Authorization: `Bearer ${spotifyToken}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(profile => {
          analytics.spotifyConnected(profile?.email, profile?.display_name);
        })
        .catch(() => {
          analytics.spotifyConnected();
        });

      const timer = setTimeout(() => {
        fetchDevices();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [spotifyToken, fetchDevices]);

  // Sync selectedTrack with currentTrack if something is already playing
  useEffect(() => {
    if (currentTrack && !selectedTrack) {
      // Fetch full track info to get duration
      const fetchTrackInfo = async () => {
        if (!spotifyToken) return;

        try {
          const trackId = currentTrack.id;
          const response = await fetch(`https://api.spotify.com/v1/tracks/${trackId}`, {
            headers: {
              'Authorization': `Bearer ${spotifyToken}`,
            },
          });

          if (response.ok) {
            const trackData = await response.json();
            setSelectedTrack({
              id: currentTrack.id,
              uri: currentTrack.uri,
              name: currentTrack.name,
              artists: currentTrack.artists,
              album: currentTrack.album,
              duration_ms: trackData.duration_ms,
            });
          }
        } catch (error) {
          console.error('Failed to fetch track info:', error);
          // Fallback: set without duration
          setSelectedTrack({
            id: currentTrack.id,
            uri: currentTrack.uri,
            name: currentTrack.name,
            artists: currentTrack.artists,
            album: currentTrack.album,
            duration_ms: position + 60000, // Estimate: current position + 1 minute
          });
        }
      };

      fetchTrackInfo();
    }
  }, [currentTrack, selectedTrack, spotifyToken, position]);

  const validateFile = (selectedFile: File): boolean => {
    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'];
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(mp3|wav|ogg)$/i)) {
      setError('Please upload an audio file (MP3, WAV, or OGG)');
      analytics.audioUploadRejected('invalid_type');
      return false;
    }

    // Validate file size (max 50MB)
    if (selectedFile.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      analytics.audioUploadRejected('too_large');
      return false;
    }

    return true;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
      analytics.audioUploadStarted(selectedFile.type || 'unknown', Math.round(selectedFile.size / 1024 / 1024 * 100) / 100);

      const metadata = await extractAudioMetadata(selectedFile);
      setAudioMetadata(metadata);

      const query = buildSpotifySearchQuery(metadata, selectedFile.name);
      setSpotifySearchQuery(query);
      setShowSpotifyMatches(true);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      setError(null);
      setResult(null);
      analytics.audioUploadStarted(droppedFile.type || 'unknown', Math.round(droppedFile.size / 1024 / 1024 * 100) / 100);

      const metadata = await extractAudioMetadata(droppedFile);
      setAudioMetadata(metadata);

      const query = buildSpotifySearchQuery(metadata, droppedFile.name);
      setSpotifySearchQuery(query);
      setShowSpotifyMatches(true);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setAnalysisJob(null);

    try {
      const data = await runAudioAnalysis(file, { onStatus: setAnalysisJob, source: 'upload' });
      setResult(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze audio file';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleTrackSelect = async (track: any) => {
    setSelectedTrack(track);
    setLoading(true);
    setError(null);
    analytics.trackPlayed(track.name, 'spotify');

    try {
      if (isReady && track.uri) {
        await playTrack(track.uri);
      }

      // Note: Spotify Audio Analysis API was deprecated Nov 2024
      // For "Spotify Mode", we just play the track without analysis
      // Users should use "Upload Mode" for full analysis

    } catch (err) {
      console.error('Error playing track:', err);
      const message = err instanceof Error ? err.message : 'Failed to play track';
      setError(message);
      analytics.audioAnalysisFailed(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeek = (positionSeconds: number) => {
    // Timeline sends seconds, convert to milliseconds for Spotify API
    seek(positionSeconds * 1000);
    analytics.playbackSeeked(positionSeconds);
  };

  const handleSpotifyMatch = async (track: any) => {
    if (!file) {
      setError('No file uploaded to analyze');
      return;
    }

    setSelectedTrack(track);
    setLoading(true);
    setError(null);
    setShowSpotifyMatches(false);
    setAnalysisJob(null);

    try {
      // Spotify Audio Analysis API was deprecated Nov 2024; librosa only.
      const librosaData = await runAudioAnalysis(file, {
        onStatus: setAnalysisJob,
        source: 'spotify_match',
      });
      setResult(librosaData);

      // Step 2: Start Spotify playback if ready
      if (spotifyToken && isReady && track.uri) {
        await playTrack(track.uri);
      } else if (spotifyToken && !isReady) {
        setError('Spotify player connecting... Click Play button once analysis completes.');
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to analyze audio';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: getGradient(pageColors.music, themeMode),
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left: Name & Stats */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                {/* Name */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 400,
                      opacity: 0.8,
                      letterSpacing: '0.5px',
                    }}
                  >
                    music of
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    Christian Battaglia
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                    @cdbattags
                  </Typography>
                </Box>

                {/* Music Features Card */}
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Features
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          Audio Analysis
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Tempo, key, beats & more
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          Spotify Integration
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Search & playback sync
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          librosa Powered
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Professional-grade tools
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right: Description */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600 }}>
                  Music Analyzer
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  Professional audio analysis tool powered by librosa and FastAPI. Upload audio files to analyze tempo,
                  key, time signature, beats, and get real-time visualization of waveforms and spectrograms.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  Connect with Spotify to search tracks, sync playback with analysis data, and explore your music in new ways.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ minHeight: 'calc(100vh - 70px)', bgcolor: 'background.default', py: 8, pb: selectedTrack ? 16 : 8 }}>
        <Container maxWidth="lg">

        {/* Spotify Connection Status & Device Selector */}
        {spotifyToken && (
          <Box sx={{ maxWidth: 'md', mx: 'auto', mb: 3 }}>
            <Alert
              severity={isReady ? "success" : "info"}
              icon={<SpotifyIcon />}
            >
              {isReady ? (
                <>
                  Spotify ready for playback sync
                  {currentTrack && ` \u2022 Playing: ${currentTrack.name}`}
                </>
              ) : (
                'Connecting to Spotify player...'
              )}
            </Alert>

            <Paper sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <DevicesIcon color="action" />
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Playback Device</InputLabel>
                  <Select
                    value={
                      activeDeviceId && devices.some(d => d.id === activeDeviceId)
                        ? activeDeviceId
                        : ''
                    }
                    label="Playback Device"
                    onChange={(e) => {
                      const id = e.target.value;
                      const device = devices.find((d) => d.id === id);
                      void switchDevice(id);
                      if (device) analytics.playbackDeviceChanged(device.name, device.type || 'unknown');
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      PaperProps: { style: { maxHeight: 300 } },
                    }}
                  >
                    {devices.length === 0 && (
                      <MenuItem value="" disabled>
                        No devices found
                      </MenuItem>
                    )}
                    {devices.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.name} {device.is_active && '(Active)'}
                        {device.type && ` - ${device.type}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchDevices()}
                >
                  Refresh
                </Button>
              </Stack>
            </Paper>
          </Box>
        )}

        {/* Mode Toggle */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Chip
            label="Upload File"
            icon={<UploadIcon />}
            onClick={() => { setMode('upload'); analytics.modeSwitched('upload'); }}
            color={mode === 'upload' ? 'primary' : 'default'}
            variant={mode === 'upload' ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Spotify Playback"
            icon={<SpotifyIcon />}
            onClick={() => { setMode('spotify'); analytics.modeSwitched('spotify'); }}
            color={mode === 'spotify' ? 'success' : 'default'}
            variant={mode === 'spotify' ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
        </Box>

        {/* Spotify Section */}
        {mode === 'spotify' && (
          <Card sx={{ mb: 4, maxWidth: 'md', mx: 'auto' }}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Spotify Playback
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    ℹ️ For audio analysis, switch to Upload Mode and select a Spotify match
                  </Typography>
                </Box>

                {/* Search */}
                {spotifyToken ? (
                  <SpotifySearch
                    accessToken={spotifyToken}
                    onTrackSelect={handleTrackSelect}
                  />
                ) : (
                  <Alert severity="info">
                    Connect to Spotify using the panel in the bottom-right corner
                  </Alert>
                )}

                {/* Playback Controls */}
                {selectedTrack && (
                    <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <img
                        src={selectedTrack.album.images[0]?.url}
                        alt={selectedTrack.album.name}
                        style={{ width: 64, height: 64, borderRadius: 4 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {selectedTrack.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedTrack.artists.map((a: any) => a.name).join(', ')}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={isPlaying ? pause : resume}
                        disabled={!isReady}
                      >
                        {isPlaying ? 'Pause' : 'Play'}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Upload Section */}
        {mode === 'upload' && (
          <Card sx={{ mb: 4, maxWidth: 'md', mx: 'auto' }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h5" fontWeight="bold">
                Upload Audio File
              </Typography>

              <Paper
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.dark' : file ? 'primary.main' : 'divider',
                  bgcolor: (theme) => isDragging
                    ? alpha(theme.palette.primary.main, 0.15)
                    : file
                      ? alpha(theme.palette.primary.main, 0.08)
                      : theme.palette.background.paper,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <input
                  type="file"
                  accept="audio/*,.mp3,.wav,.ogg"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="audio-upload"
                />
                <label htmlFor="audio-upload" style={{ cursor: 'pointer', display: 'block' }}>
                  <Stack spacing={2} alignItems="center">
                    <UploadIcon
                      sx={{
                        fontSize: 64,
                        color: isDragging ? 'primary.dark' : 'primary.main',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    />
                    <Typography variant="h6">
                      {isDragging
                        ? 'Drop file here'
                        : file
                          ? file.name
                          : 'Click to upload or drag & drop'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      MP3, WAV, or OGG • Max 50MB
                    </Typography>
                  </Stack>
                </label>
              </Paper>

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {!selectedTrack && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAnalyze}
                  disabled={!file || loading}
                  startIcon={loading ? null : <AnalyticsIcon />}
                  fullWidth
                >
                  {loading ? describeAnalysisJob(analysisJob) : 'Analyze Audio'}
                </Button>
              )}

              {selectedTrack && (
                <Alert severity="info" icon={<SpotifyIcon />}>
                  <Typography variant="body2">
                    Analysis running automatically when you select a Spotify match
                  </Typography>
                </Alert>
              )}

              {loading && (
                <Stack spacing={1}>
                  <LinearProgress />
                  <Typography variant="caption" color="text.secondary">
                    {describeAnalysisJob(analysisJob)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
        )}

        {/* Linked Status Indicator */}
        {mode === 'upload' && file && selectedTrack && !showSpotifyMatches && (
          <Box sx={{ maxWidth: 'lg', mx: 'auto', mb: 4 }}>
            <Alert
              severity={spotifyToken ? (isReady ? "success" : "info") : "warning"}
              icon={<SpotifyIcon />}
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (theme) => {
                  const color = spotifyToken
                    ? (isReady ? theme.palette.success.main : theme.palette.info.main)
                    : theme.palette.warning.main;
                  return alpha(color, 0.08);
                },
                border: '1px solid',
                borderColor: (theme) => {
                  const color = spotifyToken
                    ? (isReady ? theme.palette.success.main : theme.palette.info.main)
                    : theme.palette.warning.main;
                  return alpha(color, 0.3);
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {spotifyToken ? (isReady ? '🔗 Linked & Synced' : '⏳ Connecting...') : '⚠️ Spotify Not Connected'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    📁 <strong>{file.name}</strong>{' '}
                    {result
                      ? 'analyzed with librosa'
                      : loading
                        ? describeAnalysisJob(analysisJob).toLowerCase()
                        : 'ready for librosa analysis'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    🎵 Matched: <strong>{selectedTrack.name}</strong> by {selectedTrack.artists.map((a: any) => a.name).join(', ')}
                  </Typography>
                  {!spotifyToken && (
                    <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.5 }}>
                      Connect to Spotify using the panel in the bottom-right corner
                    </Typography>
                  )}
                  {spotifyToken && !isReady && (
                    <Typography variant="caption" color="info.dark" sx={{ display: 'block', mt: 0.5 }}>
                      ⏳ Spotify player connecting... Please wait
                    </Typography>
                  )}
                  {spotifyToken && isReady && (
                    <Typography variant="caption" color="text.secondary">
                      Timeline synced • Click timeline to seek • Use controls to play
                    </Typography>
                  )}
                </Box>
                {spotifyToken && isReady && (
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={async () => {
                        if (isPlaying) {
                          pause();
                        } else if (currentTrack?.id === selectedTrack.id) {
                          resume();
                        } else {
                          await playTrack(selectedTrack.uri);
                        }
                      }}
                    >
                      {isPlaying && currentTrack?.id === selectedTrack.id ? '⏸ Pause' : '▶ Play'}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Alert>
          </Box>
        )}

        {/* Spotify Match Suggestions (after file upload) */}
        {mode === 'upload' && file && showSpotifyMatches && (
          <Box sx={{ maxWidth: 'md', mx: 'auto', mb: 4 }}>
            <SpotifyMatchSuggestions
              metadata={audioMetadata || {}}
              filename={file.name}
              searchQuery={spotifySearchQuery}
              accessToken={spotifyToken}
              onTrackSelect={handleSpotifyMatch}
              onDismiss={() => setShowSpotifyMatches(false)}
            />
          </Box>
        )}

        {/* Timeline Visualization (synced with Spotify playback if linked) */}
        {result && (
          <Box sx={{ maxWidth: 'lg', mx: 'auto', mb: 4 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WaveIcon /> Audio Timeline
                  {selectedTrack && <Chip label="Synced with Spotify" color="success" size="small" />}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    console.log('=== FULL ANALYSIS DATA ===');
                    console.log(JSON.stringify(result, null, 2));
                    alert('Analysis data logged to console! Press F12 to view.');
                  }}
                >
                  🐛 Debug Data
                </Button>
              </Box>

              {/* Quick Stats */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Duration: ${result.track.duration.toFixed(1)}s`} size="small" />
                {formatTempoChip(result.track) && (
                  <Chip label={formatTempoChip(result.track)!} size="small" />
                )}
                <Chip label={`Sections: ${result.sections.length}`} size="small" />
                <Chip label={`Segments: ${result.segments.length}`} size="small" />
                <Chip label={`Beats: ${result.beats.length}`} size="small" />
                {isCustomAnalysis(result) && (
                  <Chip label={`Tempo ticks: ${result.tempo_ticks.length}`} size="small" />
                )}
              </Box>

              <AudioTimelineChart
                data={result}
                currentPosition={position / 1000} // Always show position (in seconds)
                onSeek={isReady ? handleSeek : undefined} // Enable seek when ready
              />
            </Paper>
          </Box>
        )}

        {/* Audio Features Chart */}
        {result && (
          <Box sx={{ maxWidth: 'lg', mx: 'auto', mb: 4 }}>
            <AudioFeaturesChart data={result} />
          </Box>
        )}

        {/* Results Section */}
        {result && (
          <Card sx={{ maxWidth: 'lg', mx: 'auto' }}>
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h5" fontWeight="bold">
                  Analysis Results
                </Typography>

                <Grid container spacing={3}>
                  {/* Tempo */}
                  {result.track?.tempo && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <SpeedIcon color="primary" sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Tempo
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {formatTempoPrimary(result.track, 2)}
                            </Typography>
                            {formatTempoRange(result.track) && (
                              <Typography variant="body2" color="text.secondary">
                                Range {formatTempoRange(result.track)} BPM
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {/* Key */}
                  {result.track?.key !== undefined && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <MusicIcon color="primary" sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Key
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {NOTE_NAMES[result.track.key]} {result.track.mode === 1 ? 'Major' : 'Minor'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {/* Beats */}
                  {result.beats && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <WaveIcon color="primary" sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Beats Detected
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {result.beats.length}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {/* Sections */}
                  {result.sections && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AnalyticsIcon color="primary" sx={{ fontSize: 40 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Sections
                            </Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {result.sections.length}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                <Divider />

                {/* Raw Data */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Raw Analysis Data
                  </Typography>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(result, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        </Container>
      </Box>

      {/* Fixed Playback Footer - Synced with Timeline */}
      {selectedTrack && (
          <PlaybackFooter
            track={selectedTrack}
            isPlaying={isPlaying}
            position={position} // Current position in milliseconds
            isReady={isReady}
            volume={volume} // Current volume 0-100
            devices={devices}
            activeDeviceId={activeDeviceId}
            onDeviceChange={(id) => {
              const device = devices.find((d) => d.id === id);
              void switchDevice(id);
              if (device) analytics.playbackDeviceChanged(device.name, device.type || 'unknown');
            }}
            onRefreshDevices={fetchDevices}
            onPlayPause={async () => {
              if (isPlaying) {
                await pause();
                analytics.playbackToggled('pause', selectedTrack.name);
              } else if (currentTrack && currentTrack.id === selectedTrack.id) {
                await resume();
                analytics.playbackToggled('resume', selectedTrack.name);
              } else {
                await playTrack(selectedTrack.uri);
                analytics.playbackToggled('play', selectedTrack.name);
              }
            }}
            onSeek={(ms) => handleSeek(ms / 1000)} // Footer sends ms, handleSeek expects seconds
            onVolumeChange={(vol) => {
              void setVolume(vol);
              analytics.volumeChanged(vol);
            }}
          />
      )}
    </>
  );
}
